import { Form, Input, InputNumber, Space, Typography, Select } from "antd";
import React, { useContext, useEffect, useState } from "react";

const Bmi = ({ context, gender, form, data }) => {
  const { formData } = useContext(context);
  const [states, setStates] = useState({
    height: null,
    weight: null,
    feet: null,
    inches: null,
    heightUnit: "ft_in", // 'ft_in' or 'cm'
    heightCm: null,
  });

  useEffect(() => {
    const heightCm =
      formData?.clinical_evaluation?.bmi?.height_in_cm ||
      data?.clinical_evaluation?.bmi?.height_in_cm ||
      formData?.functional_diagnosis?.bmi?.height_in_cm ||
      null;
    const feet =
      formData?.clinical_evaluation?.bmi?.height_feet ||
      data?.clinical_evaluation?.bmi?.height_feet ||
      formData?.functional_diagnosis?.bmi?.height_feet ||
      null;
    const inches =
      formData?.clinical_evaluation?.bmi?.height_inches ||
      data?.clinical_evaluation?.bmi?.height_inches ||
      formData?.functional_diagnosis?.bmi?.height_inches ||
      null;

    setStates({
      height: heightCm,
      feet,
      inches,
      heightCm,
      heightUnit: "ft_in",
      weight:
        formData?.clinical_evaluation?.bmi?.weight ||
        data?.clinical_evaluation?.bmi?.weight ||
        formData?.functional_diagnosis?.bmi?.weight ||
        null,
    });
  }, [formData]);

  const calculateBmi = () => {
    const { height, weight } = states;
    const heightM = height / 100;
    const bmiValue = (weight / (heightM * heightM)).toFixed(1);
    let status = "Underweight";
    if (bmiValue >= 30) {
      status = "Obese";
    } else if (bmiValue >= 25) {
      status = "Overweight";
    } else if (bmiValue >= 18.5) {
      status = "Healthy Weight";
    }
    form.setFieldValue(["bmi", "bmi_value"], bmiValue);
    form.setFieldValue(["bmi", "interpretation"], status);

    return `${bmiValue} (${status})`;
  };

  const handleStates = (value, key) => {
    setStates((prev) => {
      let newState = { ...prev, [key]: value };

      if (key === "feet" || key === "inches") {
        const feet = key === "feet" ? value : prev.feet;
        const inches = key === "inches" ? value : prev.inches;
        const height = (feet * 30.48 + inches * 2.54).toFixed(1);

        newState.height = parseInt(height);
        newState.heightCm = parseInt(height);

        form.setFieldValue(["bmi", "height_in_cm"], height);
        form.setFieldValue(["bmi", "height_feet"], feet);
        form.setFieldValue(["bmi", "height_inches"], inches);
      } else if (key === "heightCm") {
        const totalInches = value / 2.54;
        const feet = Math.floor(totalInches / 12);
        const inches = Math.round(totalInches % 12);

        newState.feet = feet;
        newState.inches = inches;
        newState.height = value;

        form.setFieldValue(["bmi", "height_in_cm"], value);
        form.setFieldValue(["bmi", "height_feet"], feet);
        form.setFieldValue(["bmi", "height_inches"], inches);
      }

      return newState;
    });
  };

  const handleUnitChange = (unit) => {
    setStates((prev) => ({ ...prev, heightUnit: unit }));
  };

  const calcIBW = () => {
    const ibw = (
      gender === "Female"
        ? 45.5 + 2.3 * (states?.feet * 12 + states?.inches - 60)
        : 50 + 2.3 * (states?.feet * 12 + states?.inches - 60) || ""
    ).toFixed(0);
    form.setFieldValue(["bmi", "ibw"], ibw);
    return ibw;
  };

  return (
    <>
      <Typography>
        Anthopometric Data : {states?.feet && states?.weight && calculateBmi()}
      </Typography>
      <Form.Item name={["bmi", "bmi_value"]} hidden />
      <Form.Item name={["bmi", "interpretation"]} hidden />
      <Form.Item name={["bmi", "ibw"]} hidden />
      <Form.Item name={["bmi", "height_in_cm"]} hidden />
      <Form.Item name={["bmi", "height_feet"]} hidden />
      <Form.Item name={["bmi", "height_inches"]} hidden />
      <div className="subsection_card">
        <Space size={"large"} align="start">
          <Form.Item label="Height">
            <Space.Compact>
              <Select
                value={states.heightUnit}
                onChange={handleUnitChange}
                style={{ width: 80 }}
                options={[
                  { value: "ft_in", label: "ft/in" },
                  { value: "cm", label: "cm" },
                ]}
              />
              {states.heightUnit === "ft_in" ? (
                <Input.Group compact>
                  <InputNumber
                    placeholder="ft"
                    value={states.feet}
                    onChange={(value) => handleStates(value, "feet")}
                    style={{ width: 60 }}
                  />
                  <InputNumber
                    min={0}
                    max={11}
                    placeholder="in"
                    value={states.inches}
                    onChange={(value) => handleStates(value, "inches")}
                    style={{ width: 60 }}
                  />
                </Input.Group>
              ) : (
                <InputNumber
                  placeholder="cm"
                  value={states.heightCm}
                  onChange={(value) => handleStates(value, "heightCm")}
                  style={{ width: 120 }}
                  // addonAfter="cm"
                />
              )}
            </Space.Compact>
          </Form.Item>
          {(states?.feet || states?.heightCm) && (
            <Typography.Text style={{ alignItems: "center" }}>
              {states.heightUnit === "ft_in"
                ? `Height: ${states?.height} cm`
                : `Height: ${states?.feet}' ${states?.inches}"`}
            </Typography.Text>
          )}
        </Space>
        <br />
        <Space size={"large"}>
          <Form.Item label="Actual Body Weight (ABW)" name={["bmi", "weight"]}>
            <InputNumber
              style={{ width: 150 }}
              addonAfter="kg"
              onChange={(value) => {
                setStates((prev) => ({ ...prev, weight: value }));
              }}
            />
          </Form.Item>
          <Typography>
            Ideal Body Weight (IBW) : {states?.feet && calcIBW()} kg
          </Typography>
        </Space>
        <Form.Item name={["bmi", "value"]} hidden>
          <Input value={calculateBmi() || ""} hidden />
        </Form.Item>
      </div>
    </>
  );
};

export default Bmi;
